import produce, { Draft } from 'immer';
import { getType } from 'typesafe-actions';

import { isAdaptiveCreative, isCommonCreative, isVideoAddition } from '../common/helpers';
import { CreativeType } from '../common/typings';
import { getOptionConfig } from '../CreativeOptions/helpers';
import {
    CommonOptionGroupName,
    CommonOptionName,
    CreativeOptionData,
    CreativeOptionType
} from '../CreativeOptions/typings';

import {
    AddCreativeAction,
    applyFileChanges,
    applyGraphicCreativeFile,
    changeAddButtonAvailable,
    changeOptionEditable,
    clearErrors,
    clearOptionErrors,
    closeConstructor,
    createGraphicCreative,
    createOverlay,
    createVideoCreative,
    hideLoading,
    hideSaving,
    initConstructor,
    openConstructor,
    optionChange,
    reset,
    resetPreviewsModal,
    returnSelect,
    saveName,
    setCreativeOptions,
    setEditingFileInfo,
    shouldCreateVideoCreative,
    showError,
    showOptionErrors,
    showOptionsErrors,
    showPreviewsModal,
    toggleOptionGroupVisibility
} from './actions';
import { getAdaptiveModalPreviewItems, isOptionSmartCenterable } from './AddAdaptiveCreative/helpers';
import { processAddAdaptiveCreative } from './AddAdaptiveCreative/reducers';
import { getModalPreviewItems, isOptionCroppable } from './AddCommonCreative/helpers';
import { processAddCommonCreative } from './AddCommonCreative/reducers';
import { getVideoCreativeOptionsValue } from './AddVideoCreative/helpers';
import { processAddVideoCreative } from './AddVideoCreative/reducers';
import { getDisclaimerItem, getEditingFileInfo, getMediaSets } from './helpers';
import { mapValidationErrorToOptionsError } from './mappers';
import { selectCreativeOptionGroupValue, selectCreativeOptions, selectGraphicCreativeState } from './selectors';
import { AddCreativeState } from './typings';

export const addCreativeInitialState = (type: CreativeType = CreativeType.COMMON): AddCreativeState => {
    switch (type) {
        case CreativeType.VIDEO_ADDITION:
        case CreativeType.VIDEO_ADDITION_BAD:
            return {
                type: CreativeType.VIDEO_ADDITION,
                isSaving: false,
                isLoading: true
            };

        case CreativeType.CPM_GEOPRODUCT_BAD:
        case CreativeType.CPM_GEOPRODUCT:
            return {
                type: CreativeType.CPM_GEOPRODUCT,
                isSaving: false,
                isLoading: true
            };

        case CreativeType.ADAPTIVE:
            return {
                type: CreativeType.ADAPTIVE,
                isSaving: false,
                isLoading: true
            };

        case CreativeType.OVERLAY:
            return {
                type: CreativeType.OVERLAY,
                isSaving: false,
                isLoading: true
            };

        default:
            return {
                type,
                isSaving: false,
                isLoading: true
            };
    }
};

export function addCreative(
    state: AddCreativeState = addCreativeInitialState(),
    action: AddCreativeAction
): AddCreativeState {
    return produce(state, (draft: Draft<AddCreativeState>) => {
        processAddVideoCreative(draft, action);
        processAddCommonCreative(draft, action);
        processAddAdaptiveCreative(draft, action);

        switch (action.type) {
            case getType(openConstructor): {
                // если мы открываем адаптив или адаптивный пресет, то надо изменять тип
                if (isCommonCreative(draft.type) && action.isAdaptive) {
                    draft.type = CreativeType.ADAPTIVE;
                }

                // и обратно надо его менять на common, если открываем графический креатив
                if (isAdaptiveCreative(draft.type) && !action.isAdaptive) {
                    draft.type = CreativeType.COMMON;
                }

                return;
            }

            case getType(initConstructor): {
                draft.id = action.id;
                draft.presetId = action.presetId;
                draft.returnPage = action.from;
                draft.isLoading = true;
                draft.isSaving = false;

                return;
            }

            case getType(setCreativeOptions): {
                draft.creativeOptions = action.creativeOptions;
                const creativeOptions = draft.creativeOptions;

                if (action.presetId !== undefined) {
                    draft.presetId = action.presetId;
                }

                if (action.id !== undefined) {
                    draft.id = action.id;
                }

                draft.bundle = action.bundle;

                if (draft.type === CreativeType.VIDEO_ADDITION) {
                    creativeOptions.optionsValue = getVideoCreativeOptionsValue(
                        creativeOptions.optionsValue,
                        creativeOptions.optionsConfig,
                        draft.videoPreviewData,
                        action.isAudio
                    );

                    return;
                }

                if (draft.type === CreativeType.OVERLAY) {
                    creativeOptions.optionsValue = getVideoCreativeOptionsValue(
                        creativeOptions.optionsValue,
                        creativeOptions.optionsConfig
                    );

                    return;
                }

                return;
            }

            case getType(optionChange): {
                const { name, groupName, value } = action;
                const creativeOptions = selectCreativeOptions(draft);
                const optionGroupValue = selectCreativeOptionGroupValue(creativeOptions, groupName);

                optionGroupValue.options[name] = value;

                if (
                    creativeOptions.errors &&
                    creativeOptions.errors[groupName] &&
                    creativeOptions.errors[groupName][name]
                ) {
                    creativeOptions.errors[groupName][name] = [];
                }

                if (isVideoAddition(draft.type)) {
                    return;
                }

                if (groupName !== CommonOptionGroupName.disclaimer) {
                    return;
                }

                // для параметра предупреждение при изменении id страны и предупреждения надо менять и content (текст)
                const disclaimerConfig = getOptionConfig(creativeOptions, groupName, CreativeOptionType.Disclaimer);

                if (!disclaimerConfig || disclaimerConfig.type !== CreativeOptionType.Disclaimer) {
                    return;
                }

                const disclaimer = getDisclaimerItem(
                    disclaimerConfig.items,
                    optionGroupValue.options[CommonOptionName.disclaimerCountry],
                    name === CommonOptionName.disclaimer ? value : undefined
                );

                if (!disclaimer) {
                    return;
                }

                optionGroupValue.options[CommonOptionName.content] = disclaimer.name;
                optionGroupValue.options[CommonOptionName.disclaimer] = String(disclaimer.id);

                return;
            }

            case getType(toggleOptionGroupVisibility): {
                const creativeOptions = selectCreativeOptions(draft);
                const optionGroupValue = selectCreativeOptionGroupValue(creativeOptions, action.groupName);

                optionGroupValue.visible = !optionGroupValue.visible;

                return;
            }

            case getType(reset): {
                return addCreativeInitialState();
            }

            case getType(closeConstructor):
            case getType(returnSelect): {
                switch (draft.type) {
                    case CreativeType.ADAPTIVE:
                        return {
                            type: draft.type,
                            isLoading: true,
                            isSaving: false
                        };

                    case CreativeType.VIDEO_ADDITION:
                        return {
                            type: draft.type,
                            isLoading: true,
                            isSaving: false
                        };

                    case CreativeType.OVERLAY:
                        return {
                            type: draft.type,
                            isSaving: false,
                            isLoading: true
                        };

                    default:
                        return {
                            type: draft.type,
                            isLoading: true,
                            isSaving: false
                        };
                }
            }

            case getType(saveName): {
                const creativeOptions = selectCreativeOptions(draft);
                creativeOptions.name = action.name;

                return;
            }

            case getType(showOptionsErrors): {
                const creativeOptions = selectCreativeOptions(draft);
                creativeOptions.errors = mapValidationErrorToOptionsError(
                    action.errors,
                    creativeOptions.optionsValue,
                    isVideoAddition(draft.type)
                );

                draft.isSaving = false;
                draft.isLoading = false;

                return;
            }

            case getType(showOptionErrors): {
                const { groupName, name, errors } = action;
                const creativeOptions = selectCreativeOptions(draft);

                if (!creativeOptions.errors) {
                    creativeOptions.errors = {};
                }

                if (!creativeOptions.errors[groupName]) {
                    creativeOptions.errors[groupName] = {};
                }

                creativeOptions.errors[groupName][name] = errors;

                return;
            }

            case getType(clearOptionErrors): {
                const { groupName, name } = action;
                const creativeOptions = selectCreativeOptions(draft);

                if (!creativeOptions.errors) {
                    return;
                }

                if (!creativeOptions.errors[groupName]) {
                    return;
                }

                creativeOptions.errors[groupName][name] = [];

                return;
            }

            case getType(showError): {
                draft.error = action.error;
                draft.isSaving = false;
                draft.isLoading = false;

                return;
            }

            case getType(hideLoading): {
                draft.isLoading = false;

                return;
            }

            case getType(hideSaving): {
                draft.isSaving = false;

                if (action.createPressed) {
                    draft.createPressed = true;
                }

                return;
            }

            case getType(clearErrors): {
                draft.error = undefined;
                const creativeOptions = draft.creativeOptions;

                if (!creativeOptions) {
                    return;
                }

                creativeOptions.errors = undefined;

                return;
            }

            case getType(changeOptionEditable): {
                const creativeOptions = selectCreativeOptions(draft);
                const { name, groupName } = action;

                const option = creativeOptions.optionsConfig[groupName].options.find(
                    (config: CreativeOptionData) => config.name === name
                );
                if (!option) {
                    return;
                }

                option.editable = action.value;

                return;
            }

            case getType(changeAddButtonAvailable): {
                draft.submitEnabled = action.value;

                return;
            }

            case getType(createVideoCreative):
            case getType(createOverlay):
            case getType(createGraphicCreative): {
                draft.isSaving = true;
                draft.error = undefined;
                draft.shouldCreate = false;

                const creativeOptions = draft.creativeOptions;

                if (!creativeOptions) {
                    return;
                }

                creativeOptions.errors = undefined;

                return;
            }

            case getType(shouldCreateVideoCreative): {
                draft.shouldCreate = true;

                return;
            }

            case getType(showPreviewsModal): {
                const creativeState = selectGraphicCreativeState(draft);
                const { creativeOptions, bundle } = creativeState;
                if (!creativeOptions || !bundle) {
                    return;
                }

                creativeState.previewsModalVisible = true;

                if (!action.updatePreviews) {
                    return;
                }

                if (creativeState.type === CreativeType.ADAPTIVE) {
                    if (!creativeState.item) {
                        return;
                    }

                    creativeState.previewItems = getAdaptiveModalPreviewItems(
                        creativeState.item,
                        creativeOptions,
                        bundle
                    );

                    return;
                }

                if (!creativeState.items) {
                    return;
                }

                creativeState.previewItems = getModalPreviewItems(creativeState.items, creativeOptions, bundle);

                return;
            }

            case getType(applyFileChanges): {
                const creativeState = selectGraphicCreativeState(draft);

                creativeState.previewsModalVisible = false;

                const { previewItems, editingFileInfo } = creativeState;
                if (!previewItems || !editingFileInfo) {
                    return;
                }
                const mediaSet = editingFileInfo.mediaSetId;

                if (creativeState.type === CreativeType.ADAPTIVE) {
                    if (!creativeState.item) {
                        return;
                    }

                    creativeState.item.mediaSets[mediaSet] = previewItems[0].pcodeData.mediaSets[mediaSet];
                } else {
                    if (!creativeState.items) {
                        return;
                    }

                    creativeState.items.forEach((item, index) => {
                        item.mediaSets[mediaSet] = previewItems[index].pcodeData.mediaSets[mediaSet];
                        item.cropParameters = previewItems[index].cropParameters;
                    });
                }

                creativeState.previewItems = undefined;
                creativeState.editingFileInfo = undefined;
                creativeState.ideaFilesCropped = true;

                return;
            }

            case getType(resetPreviewsModal): {
                const creativeState = selectGraphicCreativeState(draft);

                creativeState.previewsModalVisible = false;
                creativeState.previewItems = undefined;
                creativeState.editingFileInfo = undefined;
                creativeState.createPressed = false;

                return;
            }

            case getType(applyGraphicCreativeFile): {
                const creativeState = selectGraphicCreativeState(draft);
                const creativeOptions = selectCreativeOptions(draft);

                const groupValue = creativeOptions.optionsValue.find(el => el.type === action.groupName);
                if (!groupValue) {
                    return;
                }

                if (!groupValue.mediaSet) {
                    groupValue.mediaSet = groupValue.type;
                }

                const { item } = action;
                if (item.type !== undefined) {
                    return;
                }

                const isSmartCenterable = isOptionSmartCenterable(action.groupName, creativeOptions);
                const isCroppable = isOptionCroppable(action.groupName, creativeOptions);
                const mediaSetId = groupValue.mediaSet;

                // если файл нужно обрезать, то формируем editingFileInfo
                if (isSmartCenterable || isCroppable) {
                    creativeState.editingFileInfo = getEditingFileInfo(action.editorId, mediaSetId, item);

                    return;
                }

                // иначе - обновляем mediaSets

                if (creativeState.type === CreativeType.ADAPTIVE) {
                    if (!creativeState.item) {
                        return;
                    }
                    creativeState.item.mediaSets[mediaSetId] = getMediaSets(
                        item,
                        creativeState.item.mediaSets[mediaSetId]
                    );

                    return;
                }

                if (!creativeState.items) {
                    return;
                }
                creativeState.items.forEach(i => {
                    i.mediaSets[mediaSetId] = getMediaSets(item, i.mediaSets[mediaSetId]);
                });

                return;
            }

            case getType(setEditingFileInfo): {
                const creativeState = selectGraphicCreativeState(draft);
                creativeState.editingFileInfo = action.editingFileInfo;

                return;
            }

            default:
                return;
        }
    });
}
